<!-- clinicajure-staff-app/src/components/StaffDashboard.vue -->
<template>
  <div class="dashboard-container">
    <h2>Gestión de Pacientes y Estudios</h2>

    <!-- Sección de Búsqueda/Creación de Pacientes -->
    <div class="card patient-section">
      <h3>Buscar Paciente</h3>
      <div class="search-input">
        <input 
          type="text" 
          v-model="dniInput" 
          placeholder="DNI del Paciente" 
          @keyup.enter="searchPatient"
          required 
        />
        <button @click="searchPatient">Buscar</button>
      </div>
      <p v-if="searchError" class="error-message">{{ searchError }}</p>

      <div v-if="patientLoaded">
        <div v-if="foundPatientData">
          <h4 class="patient-info-header">Paciente Encontrado:</h4>
          <p><strong>DNI:</strong> {{ foundPatientData.id }}</p>
          <p><strong>Nombre:</strong> {{ foundPatientData.nombre }} {{ foundPatientData.apellido }}</p>
          <button @click="clearPatient" class="clear-button">Seleccionar Otro Paciente</button>
        </div>
        <div v-else class="create-patient-section">
          <p>Paciente con DNI <strong>{{ dniInput }}</strong> no encontrado.</p>
          <h4>Crear Nuevo Paciente</h4>
          <input type="text" v-model="newPatientName" placeholder="Nombre" required />
          <input type="text" v-model="newPatientLastName" placeholder="Apellido" required />
          <button @click="createNewPatient">Crear Paciente</button>
          <p v-if="createError" class="error-message">{{ createError }}</p>
        </div>
      </div>
    </div>

    <!-- Sección de Subida de Estudios (Solo si hay paciente seleccionado/creado) -->
    <div v-if="foundPatientData" class="card upload-section">
      <h3>Subir Nuevo Estudio para {{ foundPatientData.nombre }} {{ foundPatientData.apellido }}</h3>
      <form @submit.prevent="uploadStudy">
        <select v-model="studyType" required>
          <option value="">Seleccionar Tipo de Estudio</option>
          <option v-for="type in studyTypes" :key="type" :value="type">{{ type }}</option>
        </select>
        <input type="date" v-model="studyDate" required />
        <input type="text" v-model="doctorName" placeholder="Doctor" required />
        <input type="file" ref="fileInput" @change="handleFileUpload" accept="image/*,application/pdf" required />
        <button type="submit" :disabled="uploading">{{ uploading ? 'Subiendo...' : 'Subir Estudio' }}</button>
        <p v-if="uploadError" class="error-message">{{ uploadError }}</p>
        <p v-if="uploadSuccess" class="success-message">{{ uploadSuccess }}</p>
      </form>
    </div>
  </div>
</template>

<script>
import { auth, db, storage } from '@/firebaseConfig'; // Importamos auth, db y storage
import { doc, getDoc, setDoc } from 'firebase/firestore'; // Funciones de Firestore
import { createUserWithEmailAndPassword } from 'firebase/auth'; // Función para crear usuarios en Auth
import { ref, uploadBytes } from 'firebase/storage'; // Funciones de Storage (¡getDownloadURL ya no!)

export default {
  name: 'StaffDashboard',
  props: ['staffUser'], // Recibimos el usuario del personal logeado
  data() {
    return {
      dniInput: '',
      searchError: null,
      patientLoaded: false, // Indica si hemos terminado de buscar al paciente
      foundPatientData: null, // Si se encuentra un paciente, aquí se guardan sus datos
      
      // Para crear nuevo paciente
      newPatientName: '',
      newPatientLastName: '',
      createError: null,

      // Para subir estudios
      studyTypes: [
        'Ecografía Ocular', 
        'Campo Visual Computarizado', 
        'Ecometría', 
        'IOL Master', 
        'OCT Tomografía Computarizada', 
        'Paquimetría', 
        'Topografía Corneal'
      ],
      studyType: '',
      studyDate: '',
      doctorName: '',
      selectedFile: null,
      uploading: false,
      uploadError: null,
      uploadSuccess: null,
    };
  },
  methods: {
    async searchPatient() {
      this.searchError = null;
      this.foundPatientData = null;
      this.patientLoaded = false;
      this.newPatientName = '';
      this.newPatientLastName = '';
      this.createError = null;
      this.resetStudyForm(); // Limpiar formulario de estudio

      if (!this.dniInput) {
        this.searchError = 'Por favor, ingresa un DNI.';
        return;
      }

      const patientDocRef = doc(db, 'pacientes', this.dniInput);
      try {
        const patientDocSnap = await getDoc(patientDocRef);
        if (patientDocSnap.exists()) {
          this.foundPatientData = { id: patientDocSnap.id, ...patientDocSnap.data() };
        } else {
          this.searchError = 'Paciente no encontrado.'; // Mensaje para indicar que se debe crear
        }
      } catch (err) {
        console.error('Error al buscar paciente:', err);
        this.searchError = 'Error al buscar paciente. Inténtalo de nuevo.';
      } finally {
        this.patientLoaded = true;
      }
    },

    async createNewPatient() {
      this.createError = null;
      if (!this.dniInput || !this.newPatientName || !this.newPatientLastName) {
        this.createError = 'Por favor, completa todos los campos para el nuevo paciente.';
        return;
      }

      const patientDNI = this.dniInput;
      const patientEmail = `${patientDNI}@clinicajure.com`;

      try {
        // 1. Crear el usuario en Firebase Authentication
        await createUserWithEmailAndPassword(auth, patientEmail, patientDNI);
        
        // 2. Crear el documento del paciente en Firestore
        await setDoc(doc(db, 'pacientes', patientDNI), {
          nombre: this.newPatientName,
          apellido: this.newPatientLastName,
          passwordChanged: false, // Por defecto, necesita cambiar la contraseña
          createdAt: new Date(),
        });

        this.foundPatientData = { 
          id: patientDNI, 
          nombre: this.newPatientName, 
          apellido: this.newPatientLastName,
          passwordChanged: false
        };
        this.newPatientName = '';
        this.newPatientLastName = '';
        alert(`Paciente ${patientDNI} creado con éxito. Usuario y contraseña inicial: ${patientDNI}.`);

      } catch (err) {
        console.error('Error al crear nuevo paciente:', err);
        if (err.code === 'auth/email-already-in-use') {
          this.createError = 'Ya existe un usuario con este DNI. Si es un error, busca el paciente.';
        } else {
          this.createError = 'Error al crear paciente. Inténtalo de nuevo.';
        }
      }
    },

    handleFileUpload(event) {
      this.selectedFile = event.target.files[0];
    },

    async uploadStudy() {
      this.uploadError = null;
      this.uploadSuccess = null;
      this.uploading = true;

      if (!this.foundPatientData || !this.studyType || !this.studyDate || !this.doctorName || !this.selectedFile) {
        this.uploadError = 'Por favor, completa todos los campos del estudio y selecciona un archivo.';
        this.uploading = false;
        return;
      }

      const patientDNI = this.foundPatientData.id;
      const fileName = `${Date.now()}_${this.selectedFile.name}`; // Nombre único para el archivo
      const storagePath = `estudios/${patientDNI}/${fileName}`;
      const fileRef = ref(storage, storagePath);

      try {
        // 1. Subir el archivo a Cloud Storage
        await uploadBytes(fileRef, this.selectedFile); // Ya no asignamos a 'snapshot'

        // 2. Guardar los metadatos del estudio en Firestore
        await setDoc(doc(db, 'estudios', `${patientDNI}_${Date.now()}`), { 
          pacienteDNI: patientDNI,
          tipoEstudio: this.studyType,
          fechaRealizacion: new Date(this.studyDate), // Convertir a objeto Date para Firestore Timestamp
          doctor: this.doctorName,
          storagePath: storagePath,
          nombreArchivo: this.selectedFile.name,
          uploadedBy: this.staffUser.email, // Quién subió el estudio
          uploadedAt: new Date(),
        });

        this.uploadSuccess = 'Estudio subido y registrado con éxito.';
        this.resetStudyForm();
        this.selectedFile = null; // Limpiar el archivo seleccionado del input

      } catch (err) {
        console.error('Error al subir estudio:', err);
        this.uploadError = 'Error al subir el estudio. Inténtalo de nuevo.';
      } finally {
        this.uploading = false;
      }
    },

    clearPatient() {
      this.dniInput = '';
      this.searchError = null;
      this.patientLoaded = false;
      this.foundPatientData = null;
      this.newPatientName = '';
      this.newPatientLastName = '';
      this.createError = null;
      this.resetStudyForm();
    },

    resetStudyForm() {
      this.studyType = '';
      this.studyDate = '';
      this.doctorName = '';
      this.selectedFile = null;
      this.uploadError = null;
      this.uploadSuccess = null;
      // Para resetear el input de tipo file de forma fiable, accedemos a él directamente.
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    }
  },
  mounted() {
    // Establecer la fecha actual por defecto en el input de fecha
    const today = new Date();
    this.studyDate = today.toISOString().split('T')[0];
  }
};
</script>

<style scoped>
.dashboard-container {
  max-width: 900px;
  margin: 20px auto;
  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: left; /* Alineación de texto a la izquierda para mejor lectura */
}

h2 {
  color: #007bff; /* Azul para el título principal del dashboard */
  text-align: center;
  margin-bottom: 30px;
}

h3 {
  color: #0056b3; /* Azul más oscuro */
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
  text-align: center;
}

h4.patient-info-header {
    color: #28a745; /* Verde para info de paciente */
    margin-top: 25px;
    margin-bottom: 15px;
}

.card {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.search-input {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  justify-content: center;
}
.search-input input[type="text"] {
    flex-grow: 1;
    max-width: 300px; /* Limitar ancho del input DNI */
}

input[type="text"],
input[type="email"],
input[type="password"],
input[type="date"],
select {
  width: calc(100% - 20px);
  padding: 10px;
  margin-bottom: 15px;
  border: 1px solid #ced4da;
  border-radius: 5px;
  font-size: 1em;
  box-sizing: border-box; /* Incluye padding y borde en el ancho */
}

input[type="file"] {
    width: 100%;
    padding: 10px 0;
    margin-bottom: 15px;
    border: 1px solid #ced4da; /* Un pequeño borde para que se vea como un campo */
    border-radius: 5px;
    background-color: #e9ecef;
}

button {
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1em;
  transition: background-color 0.2s ease;
}

button:hover {
  background-color: #0056b3;
}

button:disabled {
  background-color: #a0c0e0;
  cursor: not-allowed;
}

.clear-button {
    background-color: #6c757d; /* Gris */
    margin-top: 10px;
}
.clear-button:hover {
    background-color: #5a6268;
}

.create-patient-section {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px dashed #ced4da;
}

.error-message {
  color: #dc3545; /* Rojo */
  margin-top: 10px;
  font-weight: bold;
}

.success-message {
    color: #28a745; /* Verde */
    margin-top: 10px;
    font-weight: bold;
}
</style>

